// Fulfillment presentation — one mapping, every surface (Sep 2026).
//
// Added after RUD-ORD-000045, where a customer chose a 9:45 PM pickup
// window, paid, and then saw only "Fulfillment: pickup" on the
// confirmation while the order went straight to the kitchen. Two
// separate lessons are encoded here:
//
//   * the promise a customer was charged for must stay visible to them,
//     on every surface that shows the order;
//   * mode presentation lives in ONE place. The Retail Orders row tint,
//     the mode chip and the customer's headline all read this file, so
//     adding a mode is one entry rather than a hunt through conditional
//     class names in four components.

// Canonical stored mode -> how it is presented. The stored value is
// never changed for display; this maps it.
//
// `tint`/`border` are deliberately low-saturation: they sit BEHIND
// payment and refund status pills that already carry meaning in colour,
// and must not compete with them. Colour is never the only signal —
// every consumer also renders `label`, and `icon` gives a second
// non-colour channel.
export const MODE_PRESENTATION = {
  pickup: {
    label: "Pickup",
    icon: "🛍️",
    tint: "bg-sky-50/70",
    border: "border-l-sky-300",
    chip: "bg-sky-100 text-sky-900",
  },
  takeout: {
    label: "Takeout",
    icon: "🥡",
    tint: "bg-sky-50/70",
    border: "border-l-sky-300",
    chip: "bg-sky-100 text-sky-900",
  },
  // `take_away` is a REAL stored value (`retail/orders.py::
  // VALID_FULFILLMENT_MODES`) and the historical alias for `takeout` —
  // `ORDER_MODE_ALIASES = {"take_away": "takeout"}`, which checkout and
  // `services/scheduling/service.py` both apply. It carries no distinct
  // operational meaning, so it is presented identically rather than
  // invented into a separate concept a cook would have to learn. Listed
  // explicitly instead of leaning on the generic title-case fallback,
  // which would have printed "Take Away" — a mode name that appears
  // nowhere in this product's vocabulary. The stored value is untouched;
  // this maps it, exactly as this file's header says.
  take_away: {
    label: "Takeout",
    icon: "🥡",
    tint: "bg-sky-50/70",
    border: "border-l-sky-300",
    chip: "bg-sky-100 text-sky-900",
  },
  dine_in: {
    label: "Dine-In",
    icon: "🍽️",
    tint: "bg-amber-50/70",
    border: "border-l-amber-300",
    chip: "bg-amber-100 text-amber-900",
  },
  delivery: {
    label: "Delivery",
    icon: "🚗",
    tint: "bg-violet-50/70",
    border: "border-l-violet-300",
    chip: "bg-violet-100 text-violet-900",
  },
  shipping: {
    label: "Shipping",
    icon: "📦",
    tint: "bg-emerald-50/70",
    border: "border-l-emerald-300",
    chip: "bg-emerald-100 text-emerald-900",
  },
};

// An unknown or future mode renders neutrally and legibly rather than
// unstyled — so adding a mode server-side never produces a broken row.
export const MODE_FALLBACK = {
  label: "Order",
  icon: "•",
  tint: "",
  border: "border-l-stone-200",
  chip: "bg-stone-100 text-stone-800",
};

export function modePresentation(mode) {
  const key = String(mode || "").trim().toLowerCase();
  const found = MODE_PRESENTATION[key];
  if (found) return found;
  if (!key) return MODE_FALLBACK;
  return {
    ...MODE_FALLBACK,
    label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  };
}

export function modeLabel(mode) {
  return modePresentation(mode).label;
}

// --- the fulfillment promise ----------------------------------------------

export function isScheduledOrder(order) {
  if (!order) return false;
  if (order.fulfillment_type) return String(order.fulfillment_type).toLowerCase() === "scheduled";
  return Boolean(order.scheduling?.slot_start);
}

/** Split the stored `<ISO date> · <window>` promise label into its halves. */
function splitPromiseLabel(label) {
  const text = String(label || "");
  const idx = text.indexOf(" · ");
  return idx === -1 ? { date: "", window: text } : {
    date: text.slice(0, idx), window: text.slice(idx + 3),
  };
}

/** Human day for an ISO instant, rendered in the ORDER's timezone — not
 *  the viewer's. A customer in another zone must still read the window
 *  the restaurant promised. */
function humanDate(iso, timeZone) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      ...(timeZone ? { timeZone } : {}),
    });
  } catch (_e) {
    return "";
  }
}

/**
 * The promise for any order, in one shape.
 *
 * Returns `null` for an ASAP order so every caller's test is the same.
 * Accepts either the customer-safe `scheduled_fulfillment` block from
 * the public API or the raw `scheduling` snapshot an admin surface sees.
 */
export function fulfillmentPromise(order) {
  if (!order || !isScheduledOrder(order)) return null;

  const safe = order.scheduled_fulfillment;
  if (safe?.slot_start) {
    return {
      mode: safe.mode || modeLabel(order.fulfillment?.mode),
      headline: safe.headline || `Scheduled ${safe.mode || ""}`.trim(),
      dateLabel: safe.date_label || humanDate(safe.slot_start, safe.timezone),
      windowLabel: safe.window_label,
      timezone: safe.timezone || "",
      slotStart: safe.slot_start,
      slotEnd: safe.slot_end || "",
    };
  }

  const sched = order.scheduling || {};
  if (!sched.slot_start) return null;
  const { window } = splitPromiseLabel(sched.promise_label);
  const mode = modeLabel(sched.mode || order.fulfillment?.mode);
  return {
    mode,
    headline: `Scheduled ${mode}`,
    dateLabel: humanDate(sched.slot_start, sched.timezone),
    windowLabel: window,
    timezone: sched.timezone || "",
    slotStart: sched.slot_start,
    slotEnd: sched.slot_end || "",
    // Operational only — never rendered on a customer surface.
    activateAt: sched.activate_at || "",
    scheduleStatus: order.schedule_status || "",
  };
}

/** One compact line: "Sat, Sep 12 · 9:45-10:00 PM CDT". */
export function promiseLine(order) {
  const p = fulfillmentPromise(order);
  if (!p) return "";
  return [p.dateLabel, p.windowLabel].filter(Boolean).join(" · ");
}
